import { createContext, FC, useCallback, useEffect, useMemo, useRef } from 'react';

import { usePostHog } from 'posthog-js/react';

import { LogError } from 'src/lib';

import { useAuthContext } from '../AuthProvider';

interface FeatureFlagContextType {
    isFeatureEnabled: (featureName: string) => boolean;
}

export const FeatureFlagContext = createContext<FeatureFlagContextType>({
    isFeatureEnabled: () => {
        return false;
    },
});

export const FeatureFlagProvider: FC = ({ children }) => {
    const { user } = useAuthContext();
    const posthog = usePostHog();
    const featureFlagsRef = useRef<{ [key: string]: boolean }>({});

    useEffect(() => {
        if (user) {
            posthog.identify(user.id.toString());
        }
    }, [posthog, user]);

    const isFeatureEnabled = useCallback(
        (featureName: string): boolean => {
            if (featureFlagsRef.current[featureName] !== undefined) {
                return featureFlagsRef.current[featureName];
            }

            try {
                const isEnabled = posthog.isFeatureEnabled(featureName);
                featureFlagsRef.current[featureName] = isEnabled;
                return isEnabled;
            } catch (error) {
                LogError(error);
                return false;
            }
        },
        [posthog]
    );

    const value = useMemo(() => {
        return { isFeatureEnabled };
    }, [isFeatureEnabled]);

    return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
};
